import { Component, inject, ViewChild, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { Patient } from 'fhir/r4';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { PatientRegistrationComponent } from '../patient-registration/patient-registration.component';

import { commonImports } from '../shared/table-interface';
import { baseStatusStyles } from '../shared/statusUIIcons';
import { EmptyStateComponent } from '../shared/empty-state/empty-state.component';
import { PatientRegistrationDetailsComponent } from '../patient-registration-details/patient-registration-details.component';
import { PatientRegistrationEditComponent } from '../patient-registration-edit/patient-registration-edit.component';
import { SuccessMessageComponent } from '../shared/success-message/success-message.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { UtilityService } from '../shared/utility.service';
import { ErrorService } from '../shared/error.service';
import { AuthService } from '../shared/auth/auth.service';
import { PatientRegistrationCenterStore } from '../patient-registration-center/patient-registration-center.store';
import { authBackendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { UserApproval, PatientRegistrationData } from '../patient-registration-center/patient-registration-center.resolver';

type TableRowData = {
  status: string;
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  accountType?: string;
  requestedRole?: string;
  organizationId?: string;
  fhirResourceId?: string;
  fullName?: string;
  submittedAt?: string;
  resourceType?: string;
  name?: Array<{ family?: string; given?: string[] }>;
  gender?: string;
  birthDate?: string;
  address?: Array<any>;
  telecom?: Array<{ system?: string; value?: string }>;
  active?: boolean;
  deceasedBoolean?: boolean;
  deceasedDateTime?: string;
};

@Component({
  selector: 'app-patient-reg',
  imports: [
    ...commonImports,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatSortModule,
    CommonModule,
    EmptyStateComponent,
    PatientRegistrationComponent
  ],
  templateUrl: './patient-reg.component.html',
  styleUrls: ['../medicine-requests/medicine-requests.component.scss', './patient-reg.component.scss']
})
export class PatientRegComponent implements AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  statusStyles = baseStatusStyles;
  currentSegment: string = 'pending';

  utilityService = inject(UtilityService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  http = inject(HttpClient);
  dialog = inject(MatDialog);
  errorService = inject(ErrorService);
  _sn = inject(MatSnackBar);
  auth = inject(AuthService);
  store = inject(PatientRegistrationCenterStore);
  backendApi = inject(authBackendEndPointToken);
  private readonly terminologyBase = inject(terminologyCanonicalUrlBaseToken);
  private readonly pharmacyWalkInSystem = `${this.terminologyBase}/CodeSystem/PatientOrigin`;
  private readonly pharmacyWalkInSystemLower = this.pharmacyWalkInSystem.toLowerCase();
  private readonly pharmacyWalkInCode = 'pharmacy-walkin';
  private readonly patientTypeWalkIn = 'pharmacy walk-in';
  private readonly patientTypeNormal = 'registered patient';
  private baseRows: TableRowData[] = [];
  private activeFilters: Record<string, string[]> = {};
  activeSearchTerm = '';
  activeSearchField: string | null = null;
  searchableFields: { label: string; value: string }[] = [];
  selectedSearchField: string | null = null;

  tableDataSource = new MatTableDataSource<TableRowData>();
  tableDataLevel2 = new BehaviorSubject<TableRowData[]>([]);
  tableFilter!: Map<string, any[]>;
  tableFilterFormControlObject: { [key: string]: FormGroup } = {};
  tableColumns: string[] = [];

  private pendingColumns = [
    'name',
    'contact',
    'submitted date',
    'status'
  ];

  private approvedColumns = [
    'patient id',
    'name',
    'gender',
    'date of birth',
    'patient type',
    'status',
  ];
  private archivedColumns = [
    'patient id',
    'name',
    'status'
  ];
  pageTitle = '';
  pageSubtitle = '';
  showRegistrationForm: boolean = false;
  private refreshCurrentSegment(): void {
    window.location.reload();
  }

  ngOnInit() {
    this.tableColumns = this.pendingColumns;
    this.configureHeaderForSegment(this.currentSegment);

    this.initializeStoreFromResolver();
    this.subscribeToTabChanges();
  }

  ngAfterViewInit() {
    this.tableDataSource.paginator = this.paginator;
    this.tableDataSource.sort = this.sort;

    setTimeout(() => {
      this.updateTableDataForSegment(this.currentSegment);
    });
  }

  onRegisterButtonClick(): void {
    const currentUser = this.auth.user.getValue();
    if (currentUser && currentUser.isApproved) {
      this.loadRegistrationComponent();
    } else {
      this.router.navigate(['/register']);
    }
  }

  openPatientImport(): void {
    this.router.navigate(['/app/patient-registration/import']);
  }

  private loadRegistrationComponent(): void {
    this.showRegistrationForm = true;
    setTimeout(() => {
      const element = document.querySelector('.registration-form-container');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }

  closeRegistrationForm(): void {
    this.showRegistrationForm = false;
    this.refreshCurrentSegment();
    this.subscribeToTabChanges();
  }

  private initializeStoreFromResolver(): void {
    const resolverData = this.route.parent?.snapshot.data['patientCenter'] as PatientRegistrationData;
    if (resolverData) {
      this.store.setFromResolverData(resolverData);
    }
  }

  private subscribeToTabChanges(): void {
    const segment$ = this.route.url.pipe(
      map(segs => segs?.[0]?.path || this.route.snapshot.routeConfig?.path || 'pending'),
      distinctUntilChanged()
    );

    segment$.subscribe(segment => {
      this.currentSegment = segment;
      this.configureHeaderForSegment(segment);
      this.updateTableDataForSegment(segment);
    });
  }

  private configureHeaderForSegment(segment: string): void {
    this.activeFilters = {};
    this.tableFilter = this.getFilterMapForSegment(segment);
    this.tableFilterFormControlObject = this.buildFilterControls(this.tableFilter);
    this.applyQueryParamFiltersForSegment(segment);
    this.searchableFields = this.getSearchableFieldsForSegment(segment);
    this.selectedSearchField = this.searchableFields[0]?.value ?? null;
    this.activeSearchField = this.selectedSearchField;
  }

  private getFilterMapForSegment(segment: string): Map<string, string[]> {
    if (segment === 'approved') {
      return new Map([
        ['gender', ['male', 'female', 'other', 'unknown']],
        ['patient type', [this.patientTypeWalkIn, this.patientTypeNormal]]
      ]);
    }

    return new Map();
  }

  private getSearchableFieldsForSegment(segment: string): { label: string; value: string }[] {
    if (segment === 'approved') {
      return [
        { label: 'Patient ID', value: 'patientId' },
        { label: 'Name', value: 'name' },
        { label: 'Email', value: 'email' },
        { label: 'Phone', value: 'phone' },
        { label: 'Gender', value: 'gender' },
        { label: 'Patient Type', value: 'patientType' }
      ];
    }

    if (segment === 'pending') {
      return [
        { label: 'Name', value: 'name' },
        { label: 'Email', value: 'email' },
        { label: 'Phone', value: 'phone' }
      ];
    }

    return [
      { label: 'Name', value: 'name' },
      { label: 'Email', value: 'email' },
      { label: 'Phone', value: 'phone' }
    ];
  }

  private updateTableDataForSegment(segment: string): void {
    if (segment === 'pending') {
      this.pageTitle = 'Pending Patient Registrations';
      this.pageSubtitle = 'Review, approve or reject patients that are not yet active.';
      this.tableColumns = [...this.pendingColumns];

      this.store.pending$.subscribe(pending => {
        const enriched: TableRowData[] = pending.map(p => ({
          ...p,
          status: 'pending'
        }));

        const sorted = enriched.sort((a, b) => {
          const dateA = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
          const dateB = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
          return dateB - dateA;
        });

        this.setRows(sorted);
      });

    } else if (segment === 'approved') {
      this.pageTitle = 'Approved Patients';
      this.pageSubtitle = 'All active patients approved from registration.';
      this.tableColumns = [...this.approvedColumns];

      this.store.approved$.subscribe(approved => {
        const enriched: TableRowData[] = approved.map(p => ({
          ...p,
          status: 'approved'
        }));

        const sorted = enriched.sort((a, b) => {
          const metaA = (a as any).meta;
          const metaB = (b as any).meta;
          const dateA = metaA?.lastUpdated ? new Date(metaA.lastUpdated).getTime() : 0;
          const dateB = metaB?.lastUpdated ? new Date(metaB.lastUpdated).getTime() : 0;
          return dateB - dateA;
        });

        this.setRows(sorted);
      });

    } else if (segment === 'archived') {
      this.pageTitle = 'Archived Patients';
      this.pageSubtitle = 'Inactive patients that have been archived.';
      this.tableColumns = [...this.archivedColumns];

      this.loadArchivedPatients();

    } else if (segment === 'deceased') {
      this.pageTitle = 'Deceased Patients';
      this.pageSubtitle = 'Patients marked as deceased.';
      this.tableColumns = [...this.approvedColumns];

      this.store.deceased$.subscribe(deceased => {
        const enriched: TableRowData[] = deceased.map(p => ({
          ...p,
          status: 'deceased'
        }));

        const sorted = enriched.sort((a, b) => {
          const metaA = (a as any).meta;
          const metaB = (b as any).meta;
          const dateA = a.deceasedDateTime
            ? new Date(a.deceasedDateTime).getTime()
            : (metaA?.lastUpdated ? new Date(metaA.lastUpdated).getTime() : 0);
          const dateB = b.deceasedDateTime
            ? new Date(b.deceasedDateTime).getTime()
            : (metaB?.lastUpdated ? new Date(metaB.lastUpdated).getTime() : 0);
          return dateB - dateA;
        });

        this.setRows(sorted);
      });
    }
  }

  private loadArchivedPatients(): void {
    // CRITICAL: Emit empty array immediately so template renders
    this.setRows([]);

    console.log('Loading archived patients...'); // Debug log

    this.http.get<any[]>(`${this.backendApi}/api/admin/patients/archived`).subscribe({
      next: (archived) => {
        console.log('Archived patients received:', archived); // Debug log

        const enriched: TableRowData[] = archived.map(p => ({
          ...p,
          status: 'archived'
        }));

        const sorted = enriched.sort((a, b) => {
          const metaA = (a as any).meta;
          const metaB = (b as any).meta;
          const dateA = metaA?.lastUpdated ? new Date(metaA.lastUpdated).getTime() :
            (a.submittedAt ? new Date(a.submittedAt).getTime() : 0);
          const dateB = metaB?.lastUpdated ? new Date(metaB.lastUpdated).getTime() :
            (b.submittedAt ? new Date(b.submittedAt).getTime() : 0);
          return dateB - dateA;
        });

        this.setRows(sorted);
      },
      error: (err) => {
        console.error('Failed to load archived patients:', err);
        this.errorService.openandCloseError('Failed to load archived patients');
        // Keep empty array on error so template still renders
        this.setRows([]);
      }
    });
  }
  private buildFilterControls(filters: Map<string, string[]>): { [key: string]: FormGroup } {
    const controls: { [key: string]: FormGroup } = {};
    for (const [key, value] of filters) {
      controls[key] = new FormGroup({});
      for (const filterValue of value) {
        controls[key].addControl(filterValue, new FormControl(false));
      }
    }
    return controls;
  }

  private applyQueryParamFiltersForSegment(segment: string): void {
    if (segment !== 'approved') {
      return;
    }

    const patientTypeValues = this.route.snapshot.queryParamMap.getAll('patientType')
      .flatMap(value => value.split(','))
      .map(value => this.normalizePatientTypeFilterValue(value))
      .filter(Boolean);

    if (!patientTypeValues.length) {
      return;
    }

    this.activeFilters = {
      ...this.activeFilters,
      'patient type': patientTypeValues
    };

    const patientTypeControls = this.tableFilterFormControlObject['patient type'];
    patientTypeValues.forEach(value => {
      patientTypeControls?.get(value)?.setValue(true, { emitEvent: false });
    });
  }

  onFilterChanged(change: { filterKey: string; values: string[] }): void {
    if (!change?.filterKey) {
      return;
    }
    const normalizedKey = change.filterKey.toLowerCase();
    const normalizedValues = (change.values ?? []).map(value => (value || '').toLowerCase());
    this.activeFilters = {
      ...this.activeFilters,
      [normalizedKey]: normalizedValues.filter(Boolean)
    };
    this.applyFilters(true);
  }

  onSearchCriteriaChanged(change: { term: string; field: string | null }): void {
    this.activeSearchTerm = (change?.term ?? '').trim().toLowerCase();
    this.activeSearchField = change?.field || this.selectedSearchField;
    this.applyFilters(true);
  }

  private setRows(rows: TableRowData[] | null | undefined, resetPage: boolean = true): void {
    this.baseRows = Array.isArray(rows) ? [...rows] : [];
    this.applyFilters(resetPage);
  }

  private applyFilters(resetPage: boolean = false): void {
    const filtered = this.baseRows.filter(row => this.matchesFilters(row));
    this.tableDataLevel2.next(filtered);
    this.tableDataSource.data = filtered;
    if (resetPage && this.paginator) {
      this.paginator.firstPage();
    }
  }

  private matchesFilters(row: TableRowData): boolean {
    if (!this.matchesSearch(row)) {
      return false;
    }

    const genderFilters = this.getFilterValues('gender');
    if (genderFilters.size) {
      const gender = (row?.gender || (row as any)?.gender || '').toString().toLowerCase();
      if (!gender || !genderFilters.has(gender)) {
        return false;
      }
    }

    const statusFilters = this.getFilterValues('status');
    if (statusFilters.size) {
      const status = (row?.status || '').toString().toLowerCase();
      if (!status || !statusFilters.has(status)) {
        return false;
      }
    }

    if (this.currentSegment === 'approved') {
      const patientTypeFilters = this.getFilterValues('patient type');
      if (patientTypeFilters.size) {
        const patientType = this.getPatientType(row);
        if (!patientTypeFilters.has(patientType)) {
          return false;
        }
      }
    }

    return true;
  }

  private matchesSearch(row: TableRowData): boolean {
    if (!this.activeSearchTerm) {
      return true;
    }

    const values = this.getSearchValues(row, this.activeSearchField);
    return values.some(value => value.includes(this.activeSearchTerm));
  }

  private getSearchValues(row: TableRowData, field: string | null): string[] {
    const normalize = (value: unknown) => (value ?? '').toString().toLowerCase();
    const fullName = this.getPatientName(row);
    const email = this.getTelecomValue(row, 'email') || row.email;
    const phone = this.getTelecomValue(row, 'phone') || row.phoneNumber;

    switch (field) {
      case 'patientId':
        return [this.getPatientHospitalId(row)].map(normalize);
      case 'name':
        return [fullName].map(normalize);
      case 'email':
        return [email].map(normalize);
      case 'phone':
        return [phone].map(normalize);
      case 'gender':
        return [row.gender].map(normalize);
      case 'patientType':
        return [this.getPatientTypeLabel(row)].map(normalize);
      default:
        return [
          fullName,
          email,
          phone,
          this.getPatientHospitalId(row),
          this.getPatientTypeLabel(row)
        ].map(normalize);
    }
  }

  private getTelecomValue(row: TableRowData, system: string): string {
    const telecom = Array.isArray(row.telecom) ? row.telecom : [];
    return telecom.find(item => item?.system === system)?.value ?? '';
  }

  private getFilterValues(key: string): Set<string> {
    const normalizedKey = key.toLowerCase();
    const values = this.activeFilters[normalizedKey] ?? [];
    return new Set(values);
  }

  private normalizePatientTypeFilterValue(value: string): string {
    const normalized = (value || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
    if (['pharmacy-walkin', 'pharmacy-walk-in', 'walkin', 'walk-in'].includes(normalized)) {
      return this.patientTypeWalkIn;
    }
    if (['registered-patient', 'registered'].includes(normalized)) {
      return this.patientTypeNormal;
    }
    return '';
  }

  getPatientHospitalId(element: any): string {
    return this.utilityService.getPatientHospitalId(element) || '';
  }
  private getPatientType(row: TableRowData): string {
    const tags = (row as any)?.meta?.tag;
    if (Array.isArray(tags)) {
      const isWalkIn = tags.some(tag => {
        const system = (tag?.system || '').toString().toLowerCase();
        const code = (tag?.code || '').toString().toLowerCase();
        return system === this.pharmacyWalkInSystemLower && code === this.pharmacyWalkInCode;
      });
      if (isWalkIn) {
        return this.patientTypeWalkIn;
      }
    }
    return this.patientTypeNormal;
  }

  getPatientTypeLabel(row: TableRowData): string {
    return this.getPatientType(row) === this.patientTypeWalkIn ? 'Pharmacy Walk-In' : 'Registered Patient';
  }

 viewDetails(element: any) {
  if (
    this.currentSegment === 'approved' ||
    this.currentSegment === 'deceased' ||
    this.currentSegment === 'archived'
  ) {
    const fhirId =
      ['approved', 'deceased'].includes(this.currentSegment)
        ? element.id
        : element.fhirResourceId;

    // ── Fetch Patient + its RelatedPerson(s) in one bundle call ──
    this.http
      .get<any>(
        `${this.backendApi}/fhir/Patient?_id=${fhirId}&_revinclude=RelatedPerson:patient&_format=json`
      )
      .subscribe({
        next: (bundle) => {
          // Pull the Patient entry out of the bundle
          const entries: any[] = bundle?.entry ?? [];

          const patient = entries
            .map((e: any) => e.resource)
            .find((r: any) => r?.resourceType === 'Patient' && r?.id === fhirId);

          const linkedGuardians = entries
            .map((e: any) => e.resource)
            .filter(
              (r: any) =>
                r?.resourceType === 'RelatedPerson' &&
                (r?.patient?.reference === `Patient/${fhirId}` ||
                  r?.patient?.reference?.endsWith(`/${fhirId}`))
            );

          if (!patient) {
            this.errorService.openandCloseError('Patient record not found.');
            return;
          }

          // Attach guardians directly onto the patient object
          // so PatientRegistrationDetailsComponent can read them
          const patientWithGuardians = { ...patient, linkedGuardians };

          console.log('👁️ ViewDetails patient:', patientWithGuardians);
          console.log('👁️ LinkedGuardians:', linkedGuardians);

          const ref = this.dialog.open(PatientRegistrationDetailsComponent, {
              maxHeight: '93vh',
              width: 'min(90vw, 1280px)', // 90% of viewport width, capped at 1280px
              maxWidth: '1280px',
              panelClass: 'full-width-dialog',
              data: {
              fhirResource: patientWithGuardians,
              isPending: false,
              isViewOnly: this.currentSegment === 'deceased',
              currentSegment: this.currentSegment,
            },
          });

          ref.afterClosed().subscribe((result) => {
            if (result?.archived) {
              this.store.removeApprovedPatient(element.id);
              this._sn.openFromComponent(SuccessMessageComponent, {
                data: { message: 'Patient archived successfully.', action: 'Close' },
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
              });
            } else if (result?.reactivated) {
              this.loadArchivedPatients();
              this.fetchAndAddApprovedPatient(result.id);
              this._sn.openFromComponent(SuccessMessageComponent, {
                data: { message: 'Patient reactivated successfully.', action: 'Close' },
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
              });
            } else if (result?.deleted) {
              this.loadArchivedPatients();
              this._sn.openFromComponent(SuccessMessageComponent, {
                data: { message: 'Patient deleted successfully.', action: 'Close' },
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
              });
            } else if (result?.edit) {
              this.editPatient(result.resource);
            }
          });
        },
        error: (err) => {
          console.error('Failed to fetch patient details:', err);
          this.errorService.openandCloseError('Failed to load patient details');
        },
      });
  } else {
    // Pending patients — unchanged
    this.http
      .get(`${this.backendApi}/api/admin/approvals/${element.id}`)
      .subscribe({
        next: (response: any) => {
          const ref = this.dialog.open(PatientRegistrationDetailsComponent, {
            maxHeight: '93vh',
            width: 'min(90vw, 1280px)', // Responsive, up to 1280px (4xl)
            maxWidth: '1280px',
            panelClass: 'full-width-dialog',
            data: {
              ...response.approval,
              fhirResource: response.fhirResource,
              practitionerPurpose: false,
              isPending: true,
              currentSegment: this.currentSegment,
            },
          });

          ref.afterClosed().subscribe((result) => {
            if (result?.approved) {
              this.store.removePendingApproval(response.approval.id);
              this.refreshCurrentSegment();
            } else if (result?.rejected) {
              this.store.removePendingApproval(response.approval.id);
              this.refreshCurrentSegment();
            }
          });
        },
        error: (err) => {
          console.error('Failed to fetch approval details:', err);
          this.errorService.openandCloseError('Failed to load registration details');
        },
      });
  }
}

  private approvePatient(approval: UserApproval): void {
    this.http.post(`${this.backendApi}/api/admin/approvals/${approval.id}/approve`, {
      organizationId: approval.organizationId,
      roles: [approval.requestedRole]
    }).subscribe({
      next: (response: any) => {
        this.store.removePendingApproval(approval.id);

        if (response.fhirId) {
          this.fetchAndAddApprovedPatient(response.fhirId);
        }
        this.refreshCurrentSegment();
        this._sn.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Patient approved successfully.', action: 'Close' },
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      },
      error: (err) => {
        console.error('Failed to approve patient:', err);
        this.errorService.openandCloseError('Failed to approve patient');
      }
    });
  }

  private rejectPatient(approval: UserApproval): void {
    this.http.post(`${this.backendApi}/api/admin/approvals/${approval.id}/reject`, {
      reason: 'Application did not meet requirements.'
    }).subscribe({
      next: () => {
        this.store.removePendingApproval(approval.id);
        this.refreshCurrentSegment();
        this._sn.openFromComponent(SuccessMessageComponent, {
          data: { message: 'Patient registration rejected.', action: 'Close' },
          duration: 3500,
          horizontalPosition: 'center',
          verticalPosition: 'top'
        });
      },
      error: (err) => {
        console.error('Failed to reject patient:', err);
        this.errorService.openandCloseError('Failed to reject patient');
      }
    });
  }

  private fetchAndAddApprovedPatient(fhirId: string): void {
    this.http.get<Patient>(`${this.backendApi}/fhir/Patient/${fhirId}?_format=json`).subscribe({
      next: (patient) => {
        this.store.addApprovedPatient(patient);
      },
      error: (err) => {
        console.warn('Could not fetch newly approved patient:', err);
      }
    });
  }

  editPatient(element: Patient) {
    const ref = this.dialog.open(PatientRegistrationEditComponent, {
      data: { resource: element, context: 'patient' },
      width: '720px',
      maxWidth: '92vw',
      maxHeight: '93vh'
    });

    ref.afterClosed().subscribe(result => {
      if (result?.updated) {
        const toSave: Patient = result.resource ?? element;

        console.log('📤 Sending patient update request:', {
          url: `${this.backendApi}/api/admin/patients/${element.id}`,
          patientId: element.id,
          email: toSave.telecom?.find(t => t.system === 'email')?.value,
          payload: toSave
        });

        this.http.put<any>(`${this.backendApi}/api/admin/patients/${element.id}`, toSave).subscribe({
          next: (response) => {
            console.log('✅ Patient update response:', response);
            // ✅ Re-fetch the updated patient from FHIR to get fresh data
            this.http.get<Patient>(`${this.backendApi}/fhir/Patient/${element.id}?_format=json`).subscribe({
              next: (fresh) => {
                console.log('🔄 Refreshed patient data:', fresh);
                this.store.updateApprovedPatient(fresh);
              },
              error: (err) => console.warn('Could not refresh patient after update:', err)
            });

            this._sn.openFromComponent(SuccessMessageComponent, {
              data: {
                message: response.emailUpdated
                  ? 'Patient updated successfully. Login email also updated.'
                  : 'Patient updated successfully.',
                action: 'Close'
              },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          },
          error: (err) => {
            console.error('❌ Patient update failed:', {
              status: err.status,
              statusText: err.statusText,
              error: err.error,
              url: err.url
            });
            this.errorService.openandCloseError(
              err.error?.message || 'Failed to update patient'
            );
          }
        });
      }
    });
  }

  archivePatient(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Archive Patient',
        message: `Are you sure you want to archive ${this.getPatientName(element)}? The patient account will be deactivated but data will be preserved.`,
        confirmText: 'Archive',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.http.post(`${this.backendApi}/api/admin/patients/${element.id}/archive`, {
          reason: 'Archived by administrator'
        }).subscribe({
          next: () => {
            this.store.removeApprovedPatient(element.id);
            this.refreshCurrentSegment();
            this._sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Patient archived successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          },
          error: (err) => {
            console.error('Failed to archive patient:', err);
            this.errorService.openandCloseError('Failed to archive patient');
          }
        });
      }
    });
  }

  reactivatePatient(element: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Reactivate Patient',
        message: `Are you sure you want to reactivate ${this.getPatientName(element)}?`,
        confirmText: 'Reactivate',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.http.post(`${this.backendApi}/api/admin/patients/${element.fhirResourceId}/reactivate`, {}).subscribe({
          next: () => {
            this.loadArchivedPatients();
            this.fetchAndAddApprovedPatient(element.fhirResourceId);
            this.refreshCurrentSegment();
            this._sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Patient reactivated successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          },
          error: (err) => {
            console.error('Failed to reactivate patient:', err);
            this.errorService.openandCloseError('Failed to reactivate patient');
          }
        });
      }
    });
  }

  checkAndDeletePatient(element: any): void {
    // First check if patient has dependent data
    this.http.get<any>(`${this.backendApi}/api/admin/patients/${element.id}/check-data`).subscribe({
      next: (response) => {
        if (response.hasDependentData) {
          // Show message that patient cannot be deleted
          const details = response.details;
          let message = `Cannot delete ${this.getPatientName(element)}. This patient has existing medical records:\n\n`;

          if (details.appointments.exists) message += `• ${details.appointments.count} appointment(s)\n`;
          if (details.observations.exists) message += `• ${details.observations.count} observation(s)\n`;
          if (details.serviceRequests.exists) message += `• ${details.serviceRequests.count} service request(s)\n`;
          if (details.medicationRequests.exists) message += `• ${details.medicationRequests.count} medication request(s)\n`;
          if (details.encounters.exists) message += `• ${details.encounters.count} encounter(s)\n`;
          if (details.procedures.exists) message += `• ${details.procedures.count} procedure(s)\n`;

          message += `\nPlease archive the patient instead.`;

          this.dialog.open(ConfirmDialogComponent, {
            width: '500px',
            data: {
              title: 'Cannot Delete Patient',
              message: message,
              confirmText: 'Archive Instead',
              cancelText: 'Close',
              showCancel: true
            }
          }).afterClosed().subscribe(archiveInstead => {
            if (archiveInstead) {
              this.archivePatient(element);
            }
          });
        } else {
          // Patient has no dependent data, confirm deletion
          this.confirmDeletePatient(element);
        }
      },
      error: (err) => {
        console.error('Failed to check patient data:', err);
        this.errorService.openandCloseError('Failed to check patient data');
      }
    });
  }

  private confirmDeletePatient(element: any): void {
    console.log('conf delete', element)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Patient',
        message: `Are you sure you want to permanently delete ${this.getPatientName(element)}? This action cannot be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        isDangerous: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.http.delete(`${this.backendApi}/api/admin/patients/${element.fhirResourceId}`).subscribe({
          next: () => {
            this.store.removeApprovedPatient(element.id);
            this.refreshCurrentSegment();
            this._sn.openFromComponent(SuccessMessageComponent, {
              data: { message: 'Patient deleted successfully.', action: 'Close' },
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'top'
            });
          },
          error: (err) => {
            console.error('Failed to delete patient:', err);
            this.errorService.openandCloseError(err.error?.message || 'Failed to delete patient');
          }
        });
      }
    });
  }

  private getPatientName(element: any): string {
    if (element.name && element.name.length > 0) {
      return `${element.name[0]?.given?.[0] || ''} ${element.name[0]?.family || ''}`.trim();
    } else if (element.fullName) {
      return element.fullName;
    } else if (element.firstName && element.lastName) {
      return `${element.firstName} ${element.lastName}`;
    }
    return 'this patient';
  }

  canAddPatientRegistration(): boolean {
    return this.auth.can('patient', 'add');
  }

  showRow(row: any): void {
    console.log(row);
  }
}
